import React, { useRef } from "react";
import { Box, ButtonBase, Chip, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { ArticleOutlined, CheckCircleOutline, ChecklistOutlined, CodeOutlined, GridViewOutlined, LockOutlined } from "@mui/icons-material";
import { SDD_STAGES, SddStageKey } from "./sddWorkflow";

const stageIcons: Record<SddStageKey, React.ReactNode> = {
    specify: <ArticleOutlined fontSize="small" />,
    plan: <GridViewOutlined fontSize="small" />,
    task_breakdown: <ChecklistOutlined fontSize="small" />,
    implement: <CodeOutlined fontSize="small" />,
    validate: <CheckCircleOutline fontSize="small" />
};

const stageColors: Record<SddStageKey, string> = {
    specify: "#6366f1",
    plan: "#0284c7",
    task_breakdown: "#059669",
    implement: "#d97706",
    validate: "#7c3aed"
};

const LAPTOP_FIT_QUERY = "@media (min-width: 1280px) and (min-height: 768px)";

const displayCopy: Record<SddStageKey, { title: string; subtitle: string }> = {
    specify: { title: "Specify", subtitle: "Frame the problem before writing code." },
    plan: { title: "Plan", subtitle: "Decide the shape of the solution." },
    task_breakdown: { title: "Define Tasks", subtitle: "Turn the plan into stories and tasks." },
    implement: { title: "Implement", subtitle: "Create code one task/story at a time." },
    validate: { title: "Validate", subtitle: "Confirm the work meets the spec." }
};

export function SddWorkflowStepper({
    selected,
    completedDrafts,
    onSelect,
    driftCount = 0
}: {
    selected: SddStageKey;
    completedDrafts: ReadonlySet<SddStageKey>;
    onSelect: (stage: SddStageKey) => void;
    driftCount?: number;
}) {
    const buttonsRef = useRef<Array<HTMLButtonElement | null>>([]);
    const tablistRef = useRef<HTMLDivElement | null>(null);
    const [feedbackConnector, setFeedbackConnector] = React.useState<{ left: number; width: number } | null>(null);
    const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

    React.useLayoutEffect(() => {
        const updateConnector = () => {
            const tablist = tablistRef.current;
            const specify = buttonsRef.current[0];
            const validate = buttonsRef.current[4];
            if (!tablist || !specify || !validate) return;

            const tablistRect = tablist.getBoundingClientRect();
            const specifyRect = specify.getBoundingClientRect();
            const validateRect = validate.getBoundingClientRect();
            const left = specifyRect.left - tablistRect.left + specifyRect.width / 2;
            // The trailing Ship/Done controls make the geometric midpoint read
            // slightly right-heavy; inset the return stem to the tab's visual center.
            const right = validateRect.left - tablistRect.left + validateRect.width / 2 - 8;
            setFeedbackConnector({ left, width: Math.max(0, right - left) });
        };

        updateConnector();
        window.addEventListener("resize", updateConnector);
        const resizeObserver = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateConnector);
        if (tablistRef.current) resizeObserver?.observe(tablistRef.current);
        buttonsRef.current.forEach(button => button && resizeObserver?.observe(button));
        return () => {
            window.removeEventListener("resize", updateConnector);
            resizeObserver?.disconnect();
        };
    }, []);

    const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
        event.preventDefault();
        const next =
            event.key === "Home"
                ? 0
                : event.key === "End"
                  ? SDD_STAGES.length - 1
                  : (index + (event.key === "ArrowRight" ? 1 : -1) + SDD_STAGES.length) % SDD_STAGES.length;
        buttonsRef.current[next]?.focus();
        onSelect(SDD_STAGES[next].key);
    };

    return (
        <Box sx={{ position: "relative", minWidth: 830, pt: "24px", [LAPTOP_FIT_QUERY]: { pt: "18px" } }}>
            {feedbackConnector && (
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: feedbackConnector.left,
                        width: feedbackConnector.width,
                        height: 20,
                        boxSizing: "border-box",
                        borderTop: "1px dashed #d97706",
                        borderLeft: "1px dashed #d97706",
                        color: "#b45309",
                        pointerEvents: "none",
                        zIndex: 6,
                        "&::before": {
                            content: '\"\"',
                            position: "absolute",
                            top: -1,
                            right: -1,
                            height: 24,
                            borderRight: "1px dashed #d97706"
                        },
                        "&::after": {
                            content: '\"\"',
                            position: "absolute",
                            left: -4,
                            bottom: -2,
                            width: 7,
                            height: 7,
                            borderRight: "1px solid #d97706",
                            borderBottom: "1px solid #d97706",
                            transform: "rotate(45deg)"
                        }
                    }}
                >
                    <Typography
                        sx={{
                            position: "absolute",
                            top: 5,
                            left: "50%",
                            transform: "translateX(-50%)",
                            px: 1,
                            borderRadius: "999px",
                            bgcolor: "#fcfcfd",
                            fontSize: 10.5,
                            lineHeight: 1.45,
                            fontWeight: 600,
                            color: "#b45309",
                            whiteSpace: "nowrap"
                        }}
                    >
                        Iterate{driftCount ? ` · ${driftCount} drift found` : ""}
                    </Typography>
                </Box>
            )}
            <Box
                ref={tablistRef}
                role="tablist"
                aria-label="Specification-driven development stages"
                sx={{ display: "flex", alignItems: "stretch", gap: 0 }}
            >
                {SDD_STAGES.map((stage, index) => {
                    const active = stage.key === selected;
                    const draftDone = completedDrafts.has(stage.key);
                    const locked = stage.key === "implement" && !["specify", "plan", "task_breakdown"].every(key => completedDrafts.has(key as SddStageKey));
                    const accent = stageColors[stage.key];
                    const copy = displayCopy[stage.key];
                    const button = (
                        <ButtonBase
                            ref={element => {
                                buttonsRef.current[index] = element;
                            }}
                            role="tab"
                            aria-label={`${stage.title} SDD stage`}
                            aria-selected={active}
                            aria-controls={`sdd-panel-${stage.key}`}
                            tabIndex={active ? 0 : -1}
                            data-ui-bridge={`sdd.stage.${stage.key}`}
                            data-ui-label={`${stage.title} SDD stage`}
                            data-ui-role="tab"
                            data-ui-group="sdd-workflow"
                            onClick={() => onSelect(stage.key)}
                            onKeyDown={event => handleKeyDown(event, index)}
                            sx={{
                                position: "relative",
                                flex: 1,
                                minWidth: 124,
                                minHeight: 92,
                                p: "11px 12px 9px",
                                borderRadius: active ? "10px 10px 0 0" : "10px",
                                border: "1px solid",
                                borderBottomColor: active ? "background.paper" : "rgba(0,0,0,.07)",
                                borderColor: active ? accent : "rgba(0,0,0,.07)",
                                bgcolor: active ? "background.paper" : "#fafafa",
                                mb: active ? "-1px" : 0,
                                // Keep the selected tab above the console frame so its bottom
                                // remains open while the frame line crosses inactive tabs.
                                zIndex: active ? 5 : 1,
                                alignItems: "stretch",
                                justifyContent: "flex-start",
                                textAlign: "left",
                                transition: reduceMotion ? "none" : "border-color 150ms ease, background-color 150ms ease",
                                "&:hover": { borderColor: accent },
                                "&:focus-visible": { outline: "3px solid rgba(99,102,241,.25)", outlineOffset: 2 },
                                [LAPTOP_FIT_QUERY]: { minHeight: 68, p: "6px 8px" }
                            }}
                        >
                            <Box sx={{ width: "100%" }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.45 }}>
                                    <Box
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: "50%",
                                            display: "grid",
                                            placeItems: "center",
                                            bgcolor: active ? accent : "#f1f5f9",
                                            color: active ? "common.white" : "text.secondary",
                                            fontSize: 11,
                                            fontWeight: 700
                                        }}
                                    >
                                        {stage.number}
                                    </Box>
                                    <Box sx={{ color: active ? accent : "#94a3b8", display: "flex", "& svg": { fontSize: 15 } }}>{stageIcons[stage.key]}</Box>
                                    {draftDone && (
                                        <CheckCircleOutline aria-label="First draft complete" sx={{ ml: "auto", color: "success.main", fontSize: 17 }} />
                                    )}
                                    {locked && <LockOutlined aria-label="Implementation locked" sx={{ ml: "auto", color: "warning.dark", fontSize: 16 }} />}
                                </Box>
                                <Typography sx={{ fontSize: 12.5, fontWeight: 650, color: active ? "#0f172a" : "#475569" }}>{copy.title}</Typography>
                                <Typography sx={{ mt: 0.2, minHeight: 26, fontSize: 9.5, lineHeight: 1.35, color: "#64748b", [LAPTOP_FIT_QUERY]: { display: "none" } }}>{copy.subtitle}</Typography>
                            </Box>
                        </ButtonBase>
                    );
                    return (
                        <React.Fragment key={stage.key}>
                            {locked ? (
                                <Tooltip title="Complete first drafts for Specify, Plan, and Define Tasks before implementation.">{button}</Tooltip>
                            ) : (
                                button
                            )}
                            {index < SDD_STAGES.length - 1 && (
                                <Box aria-hidden sx={{ width: 22, flex: "0 0 22px", display: "grid", placeItems: "center", color: "#94a3b8", fontSize: 18 }}>
                                    →
                                </Box>
                            )}
                        </React.Fragment>
                    );
                })}
                <Box aria-hidden sx={{ width: 22, flex: "0 0 22px", display: "grid", placeItems: "center", color: "#94a3b8", fontSize: 18 }}>
                    →
                </Box>
                <Chip label="ship" size="small" sx={{ alignSelf: "center", height: 25, bgcolor: "#f1f5f9", color: "#64748b", fontSize: 9.5 }} />
                <Box aria-hidden sx={{ width: 22, flex: "0 0 22px", display: "grid", placeItems: "center", color: "#94a3b8", fontSize: 18 }}>
                    →
                </Box>
                <Typography sx={{ alignSelf: "center", color: "#059669", fontSize: 12, fontWeight: 600, pr: 0.5 }}>Done</Typography>
            </Box>
        </Box>
    );
}
